/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react'
import {
  Control,
  Controller,
  FormState,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetError,
  UseFormSetValue,
  UseFormTrigger,
  UseFormWatch
} from 'react-hook-form'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Autocomplete,
  Button,
  Dialog,
  Icon,
  Textfield,
  ButtonTabs
} from '@clientbase/clientbase-library'
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  Tooltip,
  Typography
} from '@mui/material'
import { DataService } from 'api/DataService'
import { useAuthContext } from 'context/AuthContext'
import CouponsAccordion from 'features/coupons/components/CouponsAccordion'
import {
  useCanUseCoupons,
  useCouponsAccess
} from 'features/coupons/hooks/useCanUseCoupons'
import { Coupon } from 'features/coupons/types/coupon'
import { AutoCompleteBillingsParams } from 'models/Billings/AutoComplete'
import ExtraFieldsBySection from 'templates/v2/RegistrationLink/components/FormLayout/ExtraFieldsBySection'
import {
  EXTRAS_SECTION,
  FormSection,
  newSection,
  sanitizeDisplaySettings
} from 'templates/v2/RegistrationLink/components/FormLayout/formLayout.utils'

import AutocompleteCustomers from 'components/AutocompleteCustomers'
import CreateGroupModal from 'components/CreateGroupModal'
import ExtraFieldsManager, {
  ExtraFieldItem
} from 'components/ExtraFieldsManager'
import ExtraFieldEditor, {
  useExtraFieldEditor
} from 'components/ExtraFieldsManager/ExtraFieldEditor'
import { ExtraFieldSkeleton } from 'components/ExtraFieldsManager/SortableExtraFieldItem'
import GroupOption, { toGroupOption } from 'components/GroupOption'
import { PaymentsTypeRef } from 'components/PaymentsType'
import SharedChargeFields from 'components/SharedChargeFields/SharedChargeFields'
import Upgrade from 'components/Upgrade'

import { getPortalBaseUrl } from 'utils/domain-urls'

// Componentes integrados do BodyModalNewChargeOrRecurrence

import { theme } from 'styles/theme'

import CreatePaymentLinkFormInterface, {
  AutocompleteParams,
  CustomerGroups
} from '../../interface'
import * as S from './styles'

type CreatePaymentLinkFormProps = {
  control: Control<CreatePaymentLinkFormInterface>
  watch: UseFormWatch<CreatePaymentLinkFormInterface>
  setValue: UseFormSetValue<CreatePaymentLinkFormInterface>
  trigger: UseFormTrigger<CreatePaymentLinkFormInterface>
  setError: UseFormSetError<CreatePaymentLinkFormInterface>
  register: UseFormRegister<CreatePaymentLinkFormInterface>
  formState: FormState<CreatePaymentLinkFormInterface>
  clearErrors: UseFormClearErrors<CreatePaymentLinkFormInterface>
  customerGroups: CustomerGroups[]
  extraFields: ExtraFieldItem[]
  setExtraFields: Dispatch<SetStateAction<ExtraFieldItem[]>>
  // SC-11487: seções do formulário do checkout
  sections: FormSection[]
  setSections: Dispatch<SetStateAction<FormSection[]>>
  // Estados necessários para o submit
  isUnique: boolean
  setIsUnique: Dispatch<SetStateAction<boolean>>
  // Estados para SharedChargeFields
  isCharge: boolean
  // useFieldArray para emails
  emailsFields: any[]
  appendEmail: (value: { email: string }) => void
  removeEmail: (index: number) => void
  // useFieldArray para contacts
  contactsFields: any[]
  appendContact: (value: { type: string; required: boolean }) => void
  removeContact: (index: number) => void
  setIsCharge: Dispatch<SetStateAction<boolean>>
  // Prop para modo de edição
  isEdit?: boolean
  hasInitialProduct?: boolean
  onProductRemoved?: (keepConfig: boolean) => void
  externalReplaceDiscounts?: (discounts: any[]) => void
  paymentsTypeRef?: React.RefObject<PaymentsTypeRef>
  contractModels?: Array<{ label: string; value: string }>
  contractValidation?: {
    loading: boolean
    blocked: boolean
    blockReason: string
    missingCustomFields: Array<{ uuid: string; title: string }>
  }
  onContractModelChange?: (uuid: string | null) => void
  onAddContractCustomField?: (field: { uuid: string; title: string }) => void
  onAddAllContractCustomFields?: () => void
  couponsEnabled?: boolean
  onCouponsEnabledChange?: (enabled: boolean) => void
  associatedCoupons?: Coupon[]
  onCouponsChange?: (coupons: Coupon[]) => void
  couponsTotalAmount?: number
}

const CreatePaymentLinkForm = ({
  control,
  watch,
  setValue,
  trigger,
  register,
  formState,
  clearErrors,
  customerGroups,
  extraFields,
  setExtraFields,
  sections,
  setSections,
  isUnique,
  setIsUnique,
  isEdit = false,
  hasInitialProduct,
  onProductRemoved,
  externalReplaceDiscounts,
  paymentsTypeRef,
  emailsFields,
  appendEmail,
  removeEmail,
  contactsFields,
  appendContact,
  removeContact,
  contractModels = [],
  contractValidation,
  onContractModelChange,
  onAddContractCustomField,
  onAddAllContractCustomFields,
  couponsEnabled = false,
  onCouponsEnabledChange,
  associatedCoupons = [],
  onCouponsChange,
  couponsTotalAmount
}: CreatePaymentLinkFormProps) => {
  const { capabilities, user } = useAuthContext()
  const canUseCoupons = useCanUseCoupons()
  const couponsAccess = useCouponsAccess()
  const contractsAccess = capabilities?.['contract.template']
  const canUseContracts = contractsAccess?.has_access ?? false

  // Estado para controlar quando mostrar o ExtraFieldsManager
  const [showSelectExtraField, setShowSelectExtraField] = useState(false)
  const [isCharge, setIsCharge] = useState<boolean>(false)
  const [isOpenDialogDeleteConfig, setIsOpenDialogDeleteConfig] =
    useState(false)

  const [isCreatingExtraField, setIsCreatingExtraField] = useState(false)

  // Notificação nasce fechada, mas abre sozinha quando o link já vem com mais de
  // um e-mail — com um só (o do usuário, no padrão) não precisa chamar atenção.
  // Só na primeira vez: depois disso quem manda é o usuário.
  const [isNotificationExpanded, setIsNotificationExpanded] = useState(false)
  const hasAutoExpandedNotification = useRef(false)
  useEffect(() => {
    if (hasAutoExpandedNotification.current || emailsFields.length <= 1) return
    hasAutoExpandedNotification.current = true
    setIsNotificationExpanded(true)
  }, [emailsFields.length])

  // SC-11487: em qual seção entra o próximo campo extra escolhido
  const sectionsRef = useRef<FormSection[]>(sections)
  sectionsRef.current = sections
  const [addTargetSection, setAddTargetSection] =
    useState<string>(EXTRAS_SECTION)

  // Função para alternar campo obrigatório
  const handleToggleRequired = (id: string, checked: boolean) => {
    setExtraFields((prev) =>
      prev.map((field) =>
        field.uuid === id ? { ...field, required: checked } : field
      )
    )
  }
  const handleDeleteInitialProduct = (keepConfig: boolean) => {
    setIsOpenDialogDeleteConfig(false)
    if (onProductRemoved) {
      onProductRemoved(keepConfig)
    }
  }
  // Função para deletar campo. Condições e cópias que dependiam dele são limpas.
  const handleDeleteField = (id: string) => {
    setExtraFields((prev) =>
      sanitizeDisplaySettings(
        prev.filter((field) => field.uuid !== id),
        sectionsRef.current
      )
    )
  }

  // Edição do campo extra direto do formulário. Trocar o tipo ou as opções
  // pode invalidar condições e cópias de outros campos.
  const setExtraFieldsSanitized = useCallback<
    Dispatch<SetStateAction<ExtraFieldItem[]>>
  >(
    (update) =>
      setExtraFields((prev) =>
        sanitizeDisplaySettings(
          typeof update === 'function' ? update(prev) : update,
          sectionsRef.current
        )
      ),
    [setExtraFields]
  )
  const extraFieldEditor = useExtraFieldEditor(setExtraFieldsSanitized)

  // Estados existentes
  const [groups, setGroups] = useState<AutoCompleteBillingsParams[]>([])
  const [createGroupModalOpen, setCreateGroupModalOpen] = useState(false)

  const prevHasProductRef = useRef(false)

  useEffect(() => {
    const currentItems = watch('items') || []
    const hasProductInFirstItem = !!currentItems[0]?.product?.value

    if (
      hasInitialProduct &&
      prevHasProductRef.current &&
      !hasProductInFirstItem
    ) {
      setIsOpenDialogDeleteConfig(true)
    }

    prevHasProductRef.current = hasProductInFirstItem
  }, [watch('items'), hasInitialProduct])

  const getGroups = useCallback(async () => {
    const response = await DataService({
      url: '/v1/customer_groups/autocomplete',
      type: 'GET'
    })

    if (response.status === 200) {
      const groupsList: AutoCompleteBillingsParams[] =
        response.data.records.map(toGroupOption)

      setGroups(groupsList)
    }
  }, [])

  const personalizedOutputUrlAccess =
    capabilities?.['product_link.personalized_output_url']
  const customDomainAccess = capabilities?.['product_link.personalized_url']

  // useEffect para sincronizar chargeType com isUnique
  useEffect(() => {
    if (isUnique) {
      setValue('chargeType', 0)
    } else {
      setValue('chargeType', 1)
    }
  }, [isUnique, setValue])

  useEffect(() => {
    if (watch('hasCustomerGroups')) {
      getGroups()
    }
    setValue('groupsValue', customerGroups)
  }, [watch('hasCustomerGroups')])

  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(
    watch('hasCustomDomain') ||
      watch('hasCustomMessage') ||
      watch('hasCustomUrl') ||
      extraFields.length > 0
      ? true
      : false
  )

  return (
    <S.FormWrapper>
      <S.FormSectionWrapper>
        <Typography
          display="flex"
          variant="mdBold"
          alignItems="center"
          gap={0.5}
        >
          Nomeie sua página
        </Typography>

        <Stack gap={2}>
          <Controller
            control={control}
            name="title"
            render={({ field, fieldState: { error } }) => {
              return (
                <Textfield
                  id="link-title"
                  helperText={error?.message}
                  error={!!error?.message}
                  placeholder="Digite aqui o título da página"
                  label={
                    <Typography display="flex" alignItems="center" gap={0.5}>
                      Título
                      <Typography
                        variant="xsLight"
                        color={theme.palette.neutral[600]}
                      >
                        (Opcional)
                      </Typography>
                    </Typography>
                  }
                  {...field}
                />
              )
            }}
          />

          <Controller
            control={control}
            name="description"
            render={({ field, fieldState: { error } }) => {
              return (
                <Box>
                  <Box display="flex" justifyContent="space-between">
                    <S.CustomLabel htmlFor="description-input">
                      Descrição
                      <Typography
                        variant="xsLight"
                        color={theme.palette.neutral[600]}
                      >
                        (Opcional)
                      </Typography>
                    </S.CustomLabel>
                  </Box>
                  <S.TextAreaCustom
                    id="description-input"
                    style={{
                      height: '90px',
                      resize: 'vertical',
                      maxHeight: '50vh'
                    }}
                    helperText={error?.message}
                    error={!!error?.message}
                    placeholder="Digite aqui a descrição da página"
                    {...field}
                  />
                </Box>
              )
            }}
          />
        </Stack>
      </S.FormSectionWrapper>

      {/* Person Type (Tipo de Cliente) */}
      {watch('customerType') === 'new' && (
        <S.FormSectionWrapper>
          <Stack gap={1}>
            <Typography variant="mdBold">Tipo de Cliente</Typography>
            <Controller
              control={control}
              name="link_type"
              render={({ field: { onChange, value } }) => (
                <ButtonTabs
                  value={value || 'BOTH'}
                  onChange={(_, newValue) => onChange(newValue)}
                  tabs={[
                    { label: 'Pessoa Física', value: 'PF' },
                    { label: 'Pessoa Jurídica', value: 'PJ' },
                    { label: 'Ambos', value: 'BOTH' }
                  ]}
                />
              )}
            />
          </Stack>
        </S.FormSectionWrapper>
      )}

      <S.FormSectionWrapper>
        <Stack gap={2} mt={2}>
          <Typography variant="mdBold">Selecione para quem é o Link</Typography>

          <Box
            sx={{
              borderRadius: '6px'
            }}
          >
            <Box mb={1}>
              <Controller
                name="customerType"
                control={control}
                render={() => (
                  <>
                    <ButtonTabs
                      value={watch('customerType') as string}
                      onChange={(_, newValue) => {
                        const newNewValue = newValue as string
                        setValue(
                          'customerType',
                          newNewValue as 'new' | 'existing'
                        )
                      }}
                      tabs={[
                        {
                          label: 'Novos clientes',
                          value: 'new'
                        },
                        {
                          label: 'Cliente existente',
                          value: 'existing'
                        }
                      ]}
                    />
                  </>
                )}
              />
            </Box>
          </Box>
        </Stack>

        {watch('customerType') === 'existing' && (
          <Controller
            control={control}
            name={'customer'}
            render={({ field: { onChange, value }, fieldState: { error } }) => {
              return (
                <AutocompleteCustomers
                  onChange={(_: React.SyntheticEvent, value: unknown) => {
                    const option = value as AutocompleteParams
                    setValue('customerUuid', option ? option.value : '')
                    onChange(option ?? { value: '', label: '' })
                  }}
                  value={value}
                  errorMessage={error?.message}
                  placeholder="Escolha um cliente"
                />
              )
            }}
          />
        )}
      </S.FormSectionWrapper>
      <S.FormSectionWrapper>
        <Box
          sx={{
            border: `1px solid ${theme.palette.neutral[700]}`,
            borderRadius: '12px',
            overflow: 'hidden'
          }}
        >
          <Box
            onClick={() => setAdvancedOptionsOpen(!advancedOptionsOpen)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              p: '12px 16px',
              bgcolor: theme.palette.neutral[900],
              borderRadius: advancedOptionsOpen ? '12px 12px 0 0' : '12px',
              borderBottom: advancedOptionsOpen
                ? `1px solid ${theme.palette.neutral[700]}`
                : 'none',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Icon
                icon="help"
                sx={{
                  fontSize: '1.2rem',
                  color: theme.palette.neutral[700]
                }}
              />
              <Box
                component="span"
                sx={{
                  color: theme.palette.neutral[200],
                  fontSize: '14px',
                  fontWeight: 600
                }}
              >
                Configurações avançadas do Link
              </Box>
            </Box>
            <Icon
              icon={advancedOptionsOpen ? 'faChevronUp' : 'faChevronDown'}
              colorSX={theme.palette.neutral[400]}
              size={2}
            />
          </Box>

          <Collapse in={advancedOptionsOpen}>
            <Stack gap={1} sx={{ p: 2 }}>
              {watch('customerType') === 'new' && (
                <Controller
                  control={control}
                  name={'hasCustomerGroups'}
                  render={({ field }) => {
                    return (
                      <>
                        {' '}
                        <Accordion
                          expanded={field.value}
                          onChange={(_, expanded) =>
                            setValue('hasCustomerGroups', expanded)
                          }
                          sx={{
                            border: '1px solid #E8EBE8',
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                            borderRadius: '8px !important',
                            backgroundColor: '#FAFBFA',
                            boxShadow: 'none',
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': {
                              margin: 0,
                              backgroundColor: '#fff'
                            }
                          }}
                        >
                          <AccordionSummary
                            hasSwitch
                            expandIcon={
                              <Switch checked={field.value} {...field} />
                            }
                            sx={{
                              '.MuiAccordionSummary-content': {
                                margin: '20px 0px'
                              }
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <Typography variant="smBold">
                                Adicionar o cliente a um grupo
                              </Typography>
                              <Tooltip
                                enterTouchDelay={0}
                                arrow
                                placement="right"
                                title="Para incluir o novo cliente em um grupo pré cadastrado"
                              >
                                <div>
                                  <Icon
                                    icon="info"
                                    sx={{
                                      width: '18px',
                                      height: '18px',
                                      mt: '2px',
                                      color: theme.palette.neutral[700],
                                      cursor: 'pointer'
                                    }}
                                  />
                                </div>
                              </Tooltip>
                            </Stack>
                          </AccordionSummary>
                          <AccordionDetails sx={{ p: 2 }}>
                            <Controller
                              name={'groupsValue'}
                              control={control}
                              render={({
                                field: { onChange, value },
                                fieldState: { error }
                              }) => (
                                <>
                                  <Autocomplete
                                    id="group_select"
                                    onChange={(_, selectedOptions) => {
                                      const filtered = (
                                        selectedOptions as AutoCompleteBillingsParams[]
                                      ).filter(
                                        (opt) =>
                                          opt.value !== '__create_new_group__'
                                      )
                                      onChange(filtered)
                                    }}
                                    value={value}
                                    options={[
                                      {
                                        label: '',
                                        value: '__create_new_group__'
                                      } as AutoCompleteBillingsParams,
                                      ...groups
                                    ]}
                                    autoHighlight
                                    getOptionLabel={(option) =>
                                      (option as AutoCompleteBillingsParams)
                                        .label || ''
                                    }
                                    isOptionEqualToValue={(option, value) =>
                                      (option as AutoCompleteBillingsParams)
                                        .value ===
                                      (value as AutoCompleteBillingsParams)
                                        .value
                                    }
                                    filterOptions={(options, state) => {
                                      const filtered = options.filter(
                                        (opt: any) => {
                                          if (
                                            opt.value === '__create_new_group__'
                                          )
                                            return true
                                          return opt.label
                                            ?.toLowerCase()
                                            .includes(
                                              state.inputValue.toLowerCase()
                                            )
                                        }
                                      )
                                      return filtered
                                    }}
                                    renderOption={(props, option) => {
                                      const optionTyped = option as {
                                        label: string
                                        value: string
                                      }
                                      if (
                                        optionTyped.value ===
                                        '__create_new_group__'
                                      ) {
                                        return (
                                          <Box
                                            component="li"
                                            key="create-new-group"
                                            {...props}
                                            onClick={(e: React.MouseEvent) => {
                                              e.stopPropagation()
                                              setCreateGroupModalOpen(true)
                                            }}
                                            sx={{
                                              width: '100%',
                                              borderBottom: `solid 1px ${theme.palette.neutral[700]}`,
                                              mt: '-8px'
                                            }}
                                          >
                                            <Stack width="100%">
                                              <Button
                                                fullWidth
                                                variantButton="ghost"
                                                sx={{
                                                  justifyContent: 'flex-start'
                                                }}
                                              >
                                                <Stack
                                                  direction="row"
                                                  gap="5px"
                                                  alignItems="center"
                                                >
                                                  <Icon
                                                    icon="add"
                                                    sx={{
                                                      color:
                                                        theme.palette
                                                          .neutral[400],
                                                      fontSize: '18px'
                                                    }}
                                                  />
                                                  <Typography
                                                    sx={{
                                                      color:
                                                        theme.palette
                                                          .neutral[400],
                                                      fontWeight: '600',
                                                      fontSize: '14px'
                                                    }}
                                                  >
                                                    Novo grupo
                                                  </Typography>
                                                </Stack>
                                              </Button>
                                            </Stack>
                                          </Box>
                                        )
                                      }
                                      return (
                                        <GroupOption
                                          liProps={props}
                                          option={optionTyped}
                                        />
                                      )
                                    }}
                                    multiple
                                    noOptionsText={
                                      <Box
                                        onClick={() =>
                                          setCreateGroupModalOpen(true)
                                        }
                                        sx={{
                                          cursor: 'pointer',
                                          mx: -2,
                                          mt: '-3px'
                                        }}
                                      >
                                        <Stack width="100%">
                                          <Button
                                            fullWidth
                                            variantButton="ghost"
                                            sx={{
                                              justifyContent: 'flex-start'
                                            }}
                                          >
                                            <Stack
                                              direction="row"
                                              gap="5px"
                                              alignItems="center"
                                            >
                                              <Icon
                                                icon="add"
                                                sx={{
                                                  color:
                                                    theme.palette.neutral[400],
                                                  fontSize: '18px'
                                                }}
                                              />
                                              <Typography
                                                sx={{
                                                  color:
                                                    theme.palette.neutral[400],
                                                  fontWeight: '600',
                                                  fontSize: '14px'
                                                }}
                                              >
                                                Novo grupo
                                              </Typography>
                                            </Stack>
                                          </Button>
                                        </Stack>
                                      </Box>
                                    }
                                    renderInput={(params) => (
                                      <Textfield
                                        helperText={error?.message}
                                        error={!!error?.message}
                                        placeholder="Selecione..."
                                        sx={{
                                          ['& .MuiInputBase-root']: {
                                            height: '100%',
                                            minHeight: '36px'
                                          }
                                        }}
                                        {...params}
                                      />
                                    )}
                                  />
                                  <CreateGroupModal
                                    open={createGroupModalOpen}
                                    onClose={() =>
                                      setCreateGroupModalOpen(false)
                                    }
                                    onGroupCreated={(newGroup) => {
                                      setGroups((prev) => [...prev, newGroup])
                                      const currentValue = value || []
                                      onChange([...currentValue, newGroup])
                                    }}
                                  />
                                </>
                              )}
                            />
                          </AccordionDetails>
                        </Accordion>
                      </>
                    )
                  }}
                />
              )}

              {/* Cupons de desconto */}
              {canUseCoupons ? (
                <CouponsAccordion
                  linkUuid={undefined}
                  couponsEnabled={couponsEnabled}
                  onCouponsEnabledChange={onCouponsEnabledChange}
                  associatedCoupons={associatedCoupons}
                  onCouponsChange={onCouponsChange}
                  totalAmount={couponsTotalAmount}
                />
              ) : (
                <Accordion
                  expanded={false}
                  sx={{
                    border: '1px solid #E8EBE8',
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: '8px !important',
                    backgroundColor: '#FAFBFA',
                    boxShadow: 'none',
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary
                    hasSwitch
                    expandIcon={<Switch checked={false} disabled />}
                    sx={{
                      cursor: 'default',
                      '.MuiAccordionSummary-content': {
                        margin: '20px 0px'
                      }
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Typography variant="smBold" sx={{ opacity: 0.6 }}>
                        Cupons de desconto
                      </Typography>
                      <Upgrade
                        resourceName="Cupons de desconto"
                        plan={couponsAccess?.plan}
                      />
                    </Stack>
                  </AccordionSummary>
                </Accordion>
              )}

              {/* Contrato */}
              {canUseContracts ? (
                <Controller
                  control={control}
                  name={'hasContract' as any}
                  render={({ field: { onChange, value } }) => (
                    <Accordion
                      expanded={value}
                      onChange={() => {
                        if (value && onContractModelChange) {
                          onContractModelChange(null)
                        }
                        onChange(!value)
                      }}
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        hasSwitch
                        expandIcon={<Switch checked={value} />}
                        sx={{
                          '.MuiAccordionSummary-content': {
                            margin: '20px 0px'
                          }
                        }}
                      >
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <Typography variant="smBold">Contrato</Typography>
                          <Tooltip
                            enterTouchDelay={0}
                            arrow
                            placement="right"
                            title="Gere um contrato automaticamente para o cliente ao concluir o cadastro"
                          >
                            <div>
                              <Icon
                                icon="info"
                                sx={{
                                  width: '18px',
                                  height: '18px',
                                  mt: '2px',
                                  color: theme.palette.neutral[700],
                                  cursor: 'pointer'
                                }}
                              />
                            </div>
                          </Tooltip>
                          <Typography
                            variant="xsBold"
                            sx={{
                              backgroundColor: theme.palette.primary.main,
                              color: '#fff',
                              padding: '2px 8px',
                              borderRadius: '8px',
                              fontSize: '10px',
                              lineHeight: '16px'
                            }}
                          >
                            NOVO
                          </Typography>
                        </Stack>
                      </AccordionSummary>
                      {value && (
                        <AccordionDetails sx={{ p: 2, pt: 1 }}>
                          <Stack gap={1.5}>
                            <Controller
                              control={control}
                              name={'contractModelUuid' as any}
                              render={({ field, fieldState: { error } }) => (
                                <Autocomplete
                                  options={contractModels}
                                  value={
                                    contractModels.find(
                                      (m) => m.value === field.value
                                    ) || null
                                  }
                                  onChange={(_, newValue: any) => {
                                    const uuid = newValue?.value || null
                                    field.onChange(uuid)
                                    onContractModelChange?.(uuid)
                                  }}
                                  getOptionLabel={(option: any) =>
                                    option?.label || ''
                                  }
                                  isOptionEqualToValue={(
                                    option: any,
                                    val: any
                                  ) => option?.value === val?.value}
                                  renderInput={(params) => (
                                    <Textfield
                                      label="Modelo de contrato"
                                      placeholder="Selecione um modelo"
                                      helperText={error?.message}
                                      error={!!error?.message}
                                      {...params}
                                    />
                                  )}
                                />
                              )}
                            />

                            {/* Comportamento do contrato */}
                            {watch('contractModelUuid' as any) && (
                              <Controller
                                control={control}
                                name={'contractBillingBehavior' as any}
                                render={({ field }) => (
                                  <Box
                                    component="fieldset"
                                    border="none"
                                    p={0}
                                    m={0}
                                  >
                                    <Typography
                                      variant="smBold"
                                      sx={{ mb: 0.5 }}
                                    >
                                      Comportamento
                                    </Typography>
                                    <RadioGroup
                                      value={
                                        field.value || 'contract_and_billing'
                                      }
                                      onChange={(e) =>
                                        field.onChange(e.target.value)
                                      }
                                      sx={{
                                        display: 'flex',
                                        flexDirection: 'row'
                                      }}
                                    >
                                      <FormControlLabel
                                        value="contract_and_billing"
                                        control={<Radio />}
                                        label="Cobrança e contrato emitidos ao mesmo tempo"
                                      />
                                      <FormControlLabel
                                        value="contract_before_billing"
                                        control={<Radio />}
                                        label="Cobrança emitida após a assinatura do contrato"
                                      />
                                    </RadioGroup>
                                  </Box>
                                )}
                              />
                            )}

                            {contractValidation?.blocked && (
                              <Box
                                sx={{
                                  p: 1.5,
                                  borderRadius: 1,
                                  backgroundColor: '#FFF5F5',
                                  border: `1px solid ${theme.palette.error.main}`
                                }}
                              >
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  gap={1}
                                >
                                  <Icon
                                    icon="info"
                                    sx={{
                                      fontSize: '1.2rem',
                                      color: theme.palette.error.main
                                    }}
                                  />
                                  <Typography
                                    variant="smLight"
                                    sx={{
                                      color: theme.palette.error.main
                                    }}
                                  >
                                    {contractValidation.blockReason}
                                  </Typography>
                                </Stack>
                              </Box>
                            )}

                            {contractValidation &&
                              !contractValidation.blocked &&
                              contractValidation.missingCustomFields?.length >
                                0 && (
                                <Box
                                  sx={{
                                    p: 1.5,
                                    borderRadius: 1,
                                    backgroundColor: '#FFF8E1',
                                    border: '1px solid #FFB300'
                                  }}
                                >
                                  <Stack gap={1}>
                                    <Typography
                                      variant="smBold"
                                      sx={{ color: '#E65100' }}
                                    >
                                      Campos obrigatórios não habilitados
                                    </Typography>

                                    <Stack gap={0.5}>
                                      {contractValidation.missingCustomFields.map(
                                        (f) => (
                                          <Stack
                                            key={f.uuid}
                                            direction="row"
                                            alignItems="center"
                                            justifyContent="space-between"
                                          >
                                            <Typography
                                              variant="smLight"
                                              sx={{
                                                color:
                                                  theme.palette.neutral[200]
                                              }}
                                            >
                                              {f.title} (campo extra)
                                            </Typography>
                                            <Button
                                              variantButton="outlined"
                                              onClick={() =>
                                                onAddContractCustomField?.(f)
                                              }
                                              sx={{
                                                minWidth: 'auto',
                                                px: 1,
                                                py: 0.25,
                                                fontSize: '0.7rem',
                                                height: '28px'
                                              }}
                                            >
                                              Adicionar
                                            </Button>
                                          </Stack>
                                        )
                                      )}
                                    </Stack>

                                    <Button
                                      variantButton="primary"
                                      onClick={() =>
                                        onAddAllContractCustomFields?.()
                                      }
                                      fullWidth
                                      sx={{
                                        mt: 0.5,
                                        fontSize: '0.75rem',
                                        height: '32px'
                                      }}
                                    >
                                      Habilitar todos
                                    </Button>
                                  </Stack>
                                </Box>
                              )}
                          </Stack>
                        </AccordionDetails>
                      )}
                    </Accordion>
                  )}
                />
              ) : (
                <Accordion
                  expanded={false}
                  sx={{
                    border: '1px solid #E8EBE8',
                    borderLeft: `3px solid ${theme.palette.primary.main}`,
                    borderRadius: '8px !important',
                    backgroundColor: '#FAFBFA',
                    boxShadow: 'none',
                    '&:before': { display: 'none' }
                  }}
                >
                  <AccordionSummary
                    hasSwitch
                    expandIcon={<Switch checked={false} disabled />}
                    sx={{
                      cursor: 'default',
                      '.MuiAccordionSummary-content': {
                        margin: '20px 0px'
                      }
                    }}
                  >
                    <Stack direction="row" alignItems="center" gap={0.5}>
                      <Typography variant="smBold" sx={{ opacity: 0.6 }}>
                        Contrato
                      </Typography>
                      <Upgrade
                        resourceName="Contrato"
                        plan={contractsAccess?.plan}
                      />
                    </Stack>
                  </AccordionSummary>
                </Accordion>
              )}

              <Controller
                control={control}
                name={'hasExtraFields'}
                render={({ field }) => {
                  return (
                    <>
                      <Accordion
                        expanded={field.value}
                        onChange={(_, expanded) => {
                          setValue('hasExtraFields', expanded)
                          // Se desativar e não há campos extras, limpar a lista
                          if (!expanded && extraFields.length === 0) {
                            setExtraFields([])
                          }
                        }}
                        sx={{
                          border: '1px solid #E8EBE8',
                          borderLeft: `3px solid ${theme.palette.primary.main}`,
                          borderRadius: '8px !important',
                          backgroundColor: '#FAFBFA',
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                          '&.Mui-expanded': {
                            margin: 0,
                            backgroundColor: '#fff'
                          }
                        }}
                      >
                        <AccordionSummary
                          hasSwitch
                          expandIcon={
                            <Switch
                              checked={field.value}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) => {
                                e.stopPropagation()
                                const checked = e.target.checked
                                setValue('hasExtraFields', checked)
                                // Se desativar, limpar os campos extras
                                if (!checked) {
                                  setExtraFields([])
                                  setSections([])
                                }
                              }}
                            />
                          }
                          sx={{
                            '.MuiAccordionSummary-content': {
                              margin: '20px 0px'
                            }
                          }}
                        >
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <Typography variant="smBold">
                              Campos extras
                            </Typography>
                            <Tooltip
                              enterTouchDelay={0}
                              arrow
                              placement="right"
                              title="Para solicitar informações extras no momento do cadastro dos seus clientes"
                            >
                              <div>
                                <Icon
                                  icon="info"
                                  sx={{
                                    width: '18px',
                                    height: '18px',
                                    mt: '2px',
                                    color: theme.palette.neutral[700],
                                    cursor: 'pointer'
                                  }}
                                />
                              </div>
                            </Tooltip>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2, pt: 1 }}>
                          <ExtraFieldsBySection
                            fields={extraFields}
                            setFields={setExtraFields}
                            sections={sections}
                            setSections={setSections}
                            basicFields={{
                              email: true,
                              phone: true,
                              // Contratação simplificada não pede endereço
                              address: !(
                                watch('customerType') === 'new' &&
                                watch('simplifiedCheckout')
                              )
                            }}
                            onToggleRequired={handleToggleRequired}
                            onDelete={handleDeleteField}
                            onEdit={extraFieldEditor.onEdit}
                            onAddToSection={(sectionKey) => {
                              setAddTargetSection(sectionKey)
                              setShowSelectExtraField(true)
                            }}
                          />

                          <ExtraFieldEditor {...extraFieldEditor.editorProps} />

                          {/* Mostra skeleton quando está criando um campo */}
                          {isCreatingExtraField && <ExtraFieldSkeleton />}

                          {showSelectExtraField || extraFields.length === 0 ? (
                            <Box mb={2}>
                              {addTargetSection !== EXTRAS_SECTION && (
                                <Typography
                                  variant="smLight"
                                  color={theme.palette.neutral[400]}
                                >
                                  Adicionando em &quot;
                                  {sections
                                    .find((s) => s.uuid === addTargetSection)
                                    ?.title.trim() || 'Seção sem nome'}
                                  &quot;
                                </Typography>
                              )}
                              <ExtraFieldsManager
                                control={control}
                                setValue={setValue}
                                items={[]}
                                onItemsChange={(newItems) => {
                                  if (newItems.length > 0) {
                                    const newField =
                                      newItems[newItems.length - 1]
                                    setExtraFields((prev) => [
                                      ...prev,
                                      {
                                        uuid: newField.uuid,
                                        name: newField.name,
                                        required: newField.required || false,
                                        field_type: newField.field_type,
                                        options: newField.options,
                                        field_configuration:
                                          newField.field_configuration,
                                        ...(addTargetSection !==
                                          EXTRAS_SECTION && {
                                          display_settings: {
                                            section: addTargetSection
                                          }
                                        })
                                      }
                                    ])
                                    setAddTargetSection(EXTRAS_SECTION)
                                    setShowSelectExtraField(false)
                                    setIsCreatingExtraField(false)
                                  }
                                }}
                                showAddButton={false}
                                addButtonText="Adicionar campo extra"
                                excludedValues={extraFields.map(
                                  (field) => field.uuid
                                )}
                                onFieldCreationStart={() =>
                                  setIsCreatingExtraField(true)
                                }
                                onFieldCreationEnd={() =>
                                  setIsCreatingExtraField(false)
                                }
                              />
                            </Box>
                          ) : (
                            <></>
                          )}

                          <Box
                            sx={{
                              width: '100%',
                              borderTop: `1px solid ${theme.palette.neutral[700]}`,
                              my: '12px'
                            }}
                          />

                          <Stack direction="row" gap="24px" flexWrap="wrap">
                            <Stack
                              onClick={() => {
                                if (
                                  showSelectExtraField ||
                                  extraFields.length === 0
                                )
                                  return
                                setAddTargetSection(EXTRAS_SECTION)
                                setShowSelectExtraField(true)
                              }}
                              sx={{
                                cursor:
                                  showSelectExtraField ||
                                  extraFields.length === 0
                                    ? 'default'
                                    : 'pointer',
                                opacity:
                                  showSelectExtraField ||
                                  extraFields.length === 0
                                    ? 0.5
                                    : 1
                              }}
                              direction="row"
                              gap="5px"
                              alignItems="center"
                            >
                              <Icon
                                icon="add"
                                sx={{
                                  color: theme.palette.neutral[400],
                                  fontSize: '18px'
                                }}
                              />
                              <Typography
                                sx={{
                                  color: theme.palette.neutral[400],
                                  fontWeight: '600',
                                  fontSize: '14px'
                                }}
                              >
                                Adicionar campo extra
                              </Typography>
                            </Stack>
                            <Stack
                              role="button"
                              tabIndex={0}
                              onClick={() =>
                                setSections((prev) => [...prev, newSection()])
                              }
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' || e.key === ' ') {
                                  e.preventDefault()
                                  setSections((prev) => [...prev, newSection()])
                                }
                              }}
                              sx={{ cursor: 'pointer' }}
                              direction="row"
                              gap="5px"
                              alignItems="center"
                            >
                              <Icon
                                icon="add"
                                sx={{
                                  color: theme.palette.neutral[400],
                                  fontSize: '18px'
                                }}
                              />
                              <Typography
                                sx={{
                                  color: theme.palette.neutral[400],
                                  fontWeight: '600',
                                  fontSize: '14px'
                                }}
                              >
                                Adicionar seção
                              </Typography>
                            </Stack>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    </>
                  )
                }}
              />

              <Controller
                control={control}
                name={'hasCustomMessage'}
                render={({ field }) => {
                  return (
                    <>
                      <Accordion
                        expanded={field.value}
                        onChange={(_, expanded) =>
                          setValue('hasCustomMessage', expanded)
                        }
                        sx={{
                          border: '1px solid #E8EBE8',
                          borderLeft: `3px solid ${theme.palette.primary.main}`,
                          borderRadius: '8px !important',
                          backgroundColor: '#FAFBFA',
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                          '&.Mui-expanded': {
                            margin: 0,
                            backgroundColor: '#fff'
                          }
                        }}
                      >
                        <AccordionSummary
                          hasSwitch
                          expandIcon={
                            <Switch checked={field.value} {...field} />
                          }
                          sx={{
                            '.MuiAccordionSummary-content': {
                              margin: '20px 0px'
                            }
                          }}
                        >
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <Typography variant="smBold">
                              Mensagem personalizada após a compra
                            </Typography>
                            <Tooltip
                              enterTouchDelay={0}
                              arrow
                              placement="right"
                              title="Você pode adicionar uma mensagem personalizada que irá aparecer na confirmação de pagamento e também no e-mail enviado no pós-compra"
                            >
                              <div>
                                <Icon
                                  icon="info"
                                  sx={{
                                    width: '18px',
                                    height: '18px',
                                    mt: '2px',
                                    color: theme.palette.neutral[700],
                                    cursor: 'pointer'
                                  }}
                                />
                              </div>
                            </Tooltip>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2 }}>
                          {watch('hasCustomMessage') && (
                            <Controller
                              control={control}
                              name={'customMessage'}
                              render={({ field, fieldState: { error } }) => {
                                return (
                                  <S.TextAreaCustom
                                    helperText={error?.message}
                                    error={!!error?.message}
                                    style={{
                                      height: '90px',
                                      resize: 'vertical',
                                      maxHeight: '50vh'
                                    }}
                                    placeholder="Digite aqui sua mensagem"
                                    {...field}
                                  />
                                )
                              }}
                            />
                          )}
                        </AccordionDetails>
                      </Accordion>
                    </>
                  )
                }}
              />

              {/* Accordion de Notificação */}
              <Accordion
                expanded={isNotificationExpanded}
                onChange={(_, expanded) => setIsNotificationExpanded(expanded)}
                sx={{
                  border: '1px solid #E8EBE8',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  borderRadius: '8px !important',
                  backgroundColor: '#FAFBFA',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0,
                    backgroundColor: '#fff'
                  }
                }}
              >
                <AccordionSummary
                  aria-controls="emails-to-notify-content"
                  id="emails-to-notify"
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="smBold">Notificação</Typography>
                    <Tooltip
                      enterTouchDelay={0}
                      arrow
                      placement="right"
                      title="Configure e-mails que receberão notificações quando um novo cadastro for realizado através deste link"
                    >
                      <div>
                        <Icon
                          icon="info"
                          sx={{
                            width: '18px',
                            height: '18px',
                            mt: '2px',
                            color: theme.palette.neutral[700],
                            cursor: 'pointer'
                          }}
                        />
                      </div>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails sx={{ p: 2, pt: 1 }}>
                  <Stack gap={1}>
                    <Stack mt={1}>
                      {emailsFields.map((field, index) => (
                        <Stack
                          key={field.id}
                          direction="row"
                          gap={3}
                          alignItems="flex-start"
                        >
                          <Box flex={1}>
                            <Controller
                              control={control}
                              name={`emails_to_notify.${index}.email`}
                              render={({
                                field: { onChange, value },
                                fieldState: { error }
                              }) => (
                                <Textfield
                                  label="E-mail"
                                  placeholder="Digite o e-mail"
                                  value={value}
                                  onChange={onChange}
                                  error={!!error}
                                  helperText={error?.message || ' '}
                                />
                              )}
                            />
                          </Box>

                          <Box display="flex" alignItems="center" mt={1.85}>
                            <IconButton onClick={() => removeEmail(index)}>
                              <Icon
                                icon="close"
                                sx={{
                                  cursor: 'pointer',
                                  color: theme.palette.neutral[400],

                                  '&:hover': {
                                    color: theme.palette.neutral[300]
                                  }
                                }}
                              />
                            </IconButton>
                          </Box>
                        </Stack>
                      ))}
                    </Stack>

                    <Box
                      sx={{
                        height: '1px',
                        mt: emailsFields.length > 0 ? '-10px' : 0,
                        bgcolor: theme.palette.neutral[700]
                      }}
                    />

                    <Button
                      onClick={() => appendEmail({ email: '' })}
                      variantButton="ghost"
                      sx={{ width: 'fit-content' }}
                    >
                      <Stack direction="row" gap="5px" alignItems="center">
                        <Icon
                          icon="add"
                          sx={{
                            color: theme.palette.neutral[400],
                            fontSize: '18px'
                          }}
                        />
                        <Typography
                          sx={{
                            color: theme.palette.neutral[400],
                            fontWeight: '600',
                            fontSize: '14px'
                          }}
                        >
                          Adicionar e-mail
                        </Typography>
                      </Stack>
                    </Button>
                  </Stack>
                </AccordionDetails>
              </Accordion>

              {/* Contatos */}
              {watch('customerType') === 'new' && (
                <Controller
                  control={control}
                  name="contacts_enabled"
                  render={({ field }) => {
                    return (
                      <Accordion
                        expanded={field.value}
                        onChange={(_, expanded) => {
                          setValue('contacts_enabled', expanded)

                          // Ao ligar já traz uma linha pra preencher, em vez
                          // de abrir vazio e obrigar a clicar em "Adicionar".
                          if (expanded && contactsFields.length === 0) {
                            appendContact({ type: '', required: false })
                          }

                          // Ao desligar, linhas sem tipo saem: o schema exige
                          // tipo em toda linha e travaria o salvar.
                          if (!expanded) {
                            const contacts = watch('contacts') || []
                            contacts
                              .map((contact, idx) => (contact?.type ? -1 : idx))
                              .filter((idx) => idx >= 0)
                              .reverse()
                              .forEach((idx) => removeContact(idx))
                          }
                        }}
                        sx={{
                          border: '1px solid #E8EBE8',
                          borderLeft: `3px solid ${theme.palette.primary.main}`,
                          borderRadius: '8px !important',
                          backgroundColor: '#FAFBFA',
                          boxShadow: 'none',
                          '&:before': { display: 'none' },
                          '&.Mui-expanded': {
                            margin: 0,
                            backgroundColor: '#fff'
                          }
                        }}
                      >
                        <AccordionSummary
                          hasSwitch
                          expandIcon={<Switch checked={field.value} />}
                          sx={{
                            '.MuiAccordionSummary-content': {
                              margin: '20px 0px'
                            }
                          }}
                        >
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <Typography variant="smBold">Contatos</Typography>
                            <Tooltip
                              enterTouchDelay={0}
                              arrow
                              placement="right"
                              title="Para cada tipo de contato adicionado, serão solicitados do novo cliente o nome, telefone e email. Por padrão, o envio de notificações é desativado para contatos criados pelo link de contratação."
                            >
                              <div>
                                <Icon
                                  icon="info"
                                  sx={{
                                    width: '18px',
                                    height: '18px',
                                    mt: '2px',
                                    color: theme.palette.neutral[700],
                                    cursor: 'pointer'
                                  }}
                                />
                              </div>
                            </Tooltip>
                          </Stack>
                        </AccordionSummary>
                        <AccordionDetails sx={{ p: 2, pt: 1 }}>
                          <Stack gap={1}>
                            <Stack mt={1}>
                              {contactsFields.map((field, index) => (
                                <Stack
                                  key={field.id}
                                  direction="row"
                                  gap={3}
                                  alignItems="flex-start"
                                >
                                  <Box flex={1}>
                                    <Controller
                                      control={control}
                                      name={`contacts.${index}.type`}
                                      render={({
                                        field: { onChange, value },
                                        fieldState: { error }
                                      }) => (
                                        <Textfield
                                          placeholder="Digite o tipo de contato"
                                          value={value}
                                          onChange={onChange}
                                          error={!!error}
                                          helperText={error?.message || ' '}
                                        />
                                      )}
                                    />
                                  </Box>

                                  <Box display="flex" alignItems="center">
                                    <Controller
                                      control={control}
                                      name={`contacts.${index}.required`}
                                      render={({
                                        field: { onChange, value }
                                      }) => (
                                        <Checkbox
                                          checked={value}
                                          onChange={onChange}
                                          size="small"
                                          id={`contact-required-${index}`}
                                        />
                                      )}
                                    />
                                    <label
                                      htmlFor={`contact-required-${index}`}
                                      style={{
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center'
                                      }}
                                    >
                                      <Typography
                                        variant="smLight"
                                        color={theme.palette.neutral[400]}
                                      >
                                        Obrigatório
                                      </Typography>
                                    </label>
                                  </Box>

                                  <Box display="flex" alignItems="center">
                                    <IconButton
                                      onClick={() => removeContact(index)}
                                    >
                                      <Icon
                                        icon="close"
                                        sx={{
                                          cursor: 'pointer',
                                          color: theme.palette.neutral[400],
                                          '&:hover': {
                                            color: theme.palette.neutral[300]
                                          }
                                        }}
                                      />
                                    </IconButton>
                                  </Box>
                                </Stack>
                              ))}
                            </Stack>

                            <Box
                              sx={{
                                height: '1px',
                                mt: contactsFields.length > 0 ? '-10px' : 0,
                                bgcolor: theme.palette.neutral[700]
                              }}
                            />

                            <Button
                              onClick={() =>
                                appendContact({ type: '', required: false })
                              }
                              variantButton="ghost"
                              sx={{ width: 'fit-content' }}
                            >
                              <Stack
                                direction="row"
                                gap="5px"
                                alignItems="center"
                              >
                                <Icon
                                  icon="add"
                                  sx={{
                                    color: theme.palette.neutral[400],
                                    fontSize: '18px'
                                  }}
                                />
                                <Typography
                                  sx={{
                                    color: theme.palette.neutral[400],
                                    fontWeight: '600',
                                    fontSize: '14px'
                                  }}
                                >
                                  Adicionar contato
                                </Typography>
                              </Stack>
                            </Button>
                          </Stack>
                        </AccordionDetails>
                      </Accordion>
                    )
                  }}
                />
              )}

              {/* Comportamento no caso de cliente existente */}
              <Accordion
                disabled={watch('customerType') === 'existing'}
                sx={{
                  border: '1px solid #E8EBE8',
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  borderRadius: '8px !important',
                  backgroundColor: '#FAFBFA',
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0,
                    backgroundColor: '#fff'
                  },
                  ':disabled': { backgroundColor: '#fff' },
                  '&.Mui-disabled': {
                    backgroundColor: '#fff'
                  }
                }}
              >
                <AccordionSummary
                  aria-controls="existing-document-behavior-content"
                  id="existing-document-behavior"
                  sx={{
                    '&.Mui-disabled': {
                      opacity: 0.75
                    }
                  }}
                >
                  <Stack direction="row" alignItems="center" gap={0.5}>
                    <Typography variant="smBold">
                      Atualização Cadastral
                    </Typography>
                    <Tooltip
                      enterTouchDelay={0}
                      arrow
                      placement="right"
                      title="Defina o que ocorre quando alguém se cadastrar com um documento (CPF/CNPJ) já existente."
                    >
                      <span>
                        <Icon
                          icon="info"
                          sx={{
                            width: '18px',
                            height: '18px',
                            mt: '2px',
                            color: theme.palette.neutral[700],
                            cursor:
                              watch('customerType') === 'existing'
                                ? 'default'
                                : 'pointer',
                            pointerEvents: 'auto'
                          }}
                        />
                      </span>
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <Controller
                    control={control}
                    name="existing_document_behavior"
                    render={({ field: { onChange, value } }) => (
                      <Box component="fieldset" border="none" p={0} m={0}>
                        <Box display="flex" flexDirection="column" gap={2}>
                          <RadioGroup
                            value={value}
                            onChange={(e) => onChange(Number(e.target.value))}
                            sx={{ display: 'flex', flexDirection: 'row' }}
                          >
                            <FormControlLabel
                              value={0}
                              control={<Radio />}
                              label="Não permitir"
                            />
                            <FormControlLabel
                              value={1}
                              control={<Radio />}
                              label="Criar novo cadastro"
                            />
                            <FormControlLabel
                              value={2}
                              control={<Radio />}
                              label="Atualizar dados"
                            />
                          </RadioGroup>
                        </Box>
                      </Box>
                    )}
                  />
                </AccordionDetails>
              </Accordion>

              <Controller
                control={control}
                name={'hasCustomUrl'}
                render={({ field }) => {
                  return (
                    <Accordion
                      expanded={field.value}
                      onChange={(_, expanded) =>
                        setValue('hasCustomUrl', expanded)
                      }
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        hasSwitch
                        expandIcon={<Switch checked={field.value} {...field} />}
                        sx={{
                          '.MuiAccordionSummary-content': {
                            margin: '20px 0px'
                          }
                        }}
                      >
                        {' '}
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <Typography variant="smBold">
                            Adicionar URL de saída
                          </Typography>
                          <Tooltip
                            enterTouchDelay={0}
                            arrow
                            placement="right"
                            title="Personalizando a URL de saída, é possível indicar uma URL externa para que o cliente seja redirecionado ao reconhecermos o pagamento do PIX ou Cartão de Crédito"
                          >
                            <div>
                              <Icon
                                icon="info"
                                sx={{
                                  width: '18px',
                                  height: '18px',
                                  mt: '2px',
                                  color: theme.palette.neutral[700],
                                  cursor: 'pointer'
                                }}
                              />
                            </div>
                          </Tooltip>
                          {!personalizedOutputUrlAccess?.has_access && (
                            <Upgrade
                              resourceName="Personalizar URL de saída"
                              plan={personalizedOutputUrlAccess?.plan}
                            />
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          p: 2
                        }}
                      >
                        {personalizedOutputUrlAccess?.has_access && (
                          <Box>
                            <Controller
                              control={control}
                              name={'customUrl'}
                              render={({ field, fieldState: { error } }) => {
                                return (
                                  <Textfield
                                    helperText={error?.message}
                                    error={!!error?.message}
                                    placeholder="Digite aqui sua url de saída"
                                    disabled={
                                      !personalizedOutputUrlAccess?.has_access
                                    }
                                    {...field}
                                  />
                                )
                              }}
                            />
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )
                }}
              />
              <Controller
                control={control}
                name={'hasCustomDomain'}
                render={({ field }) => {
                  return (
                    <Accordion
                      expanded={field.value || !!user?.subdomain}
                      onChange={(_, expanded) =>
                        setValue('hasCustomDomain', expanded)
                      }
                      sx={{
                        border: '1px solid #E8EBE8',
                        borderLeft: `3px solid ${theme.palette.primary.main}`,
                        borderRadius: '8px !important',
                        backgroundColor: '#FAFBFA',
                        boxShadow: 'none',
                        '&:before': { display: 'none' },
                        '&.Mui-expanded': {
                          margin: 0,
                          backgroundColor: '#fff'
                        }
                      }}
                    >
                      <AccordionSummary
                        hasSwitch
                        expandIcon={
                          <Switch
                            checked={field.value || !!user?.subdomain}
                            {...field}
                          />
                        }
                        sx={{
                          '.MuiAccordionSummary-content': {
                            margin: '20px 0px'
                          }
                        }}
                      >
                        {' '}
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          <Typography variant="smBold">
                            Personalizar domínio
                          </Typography>
                          <Tooltip
                            enterTouchDelay={0}
                            arrow
                            placement="right"
                            title="Para personalizar o domínio"
                          >
                            <div>
                              <Icon
                                icon="info"
                                sx={{
                                  width: '18px',
                                  height: '18px',
                                  mt: '2px',
                                  color: theme.palette.neutral[700],
                                  cursor: 'pointer'
                                }}
                              />
                            </div>
                          </Tooltip>
                          {!customDomainAccess?.has_access && (
                            <Upgrade
                              resourceName="Domínio personalizado"
                              plan={customDomainAccess?.plan}
                            />
                          )}
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails
                        sx={{
                          p: 2
                        }}
                      >
                        {personalizedOutputUrlAccess?.has_access && (
                          <Box>
                            <Controller
                              control={control}
                              name={'customDomain'}
                              render={({ field, fieldState: { error } }) => {
                                return (
                                  <Textfield
                                    helperText={error?.message}
                                    error={!!error?.message}
                                    placeholder="www.exemplo.basedeclientes.com.br"
                                    disabled
                                    {...field}
                                    value={getPortalBaseUrl(
                                      user?.subdomain
                                    ).replace('https://', 'www.')}
                                  />
                                )
                              }}
                            />
                          </Box>
                        )}
                      </AccordionDetails>
                    </Accordion>
                  )
                }}
              />
              {watch('customerType') === 'new' && (
                <>
                  <Controller
                    control={control}
                    name={'sendWelcomeEmail'}
                    render={({ field }) => {
                      return (
                        <Accordion
                          expanded={false}
                          onChange={() => {
                            setValue('sendWelcomeEmail', !field.value)
                          }}
                          sx={{
                            border: '1px solid #E8EBE8',
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                            borderRadius: '8px !important',
                            backgroundColor: '#FAFBFA',
                            boxShadow: 'none',
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': {
                              margin: 0,
                              backgroundColor: '#fff'
                            }
                          }}
                        >
                          <AccordionSummary
                            hasSwitch
                            expandIcon={
                              <Switch checked={field.value} {...field} />
                            }
                            sx={{
                              '.MuiAccordionSummary-content': {
                                margin: '20px 0px'
                              }
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <Typography variant="smBold">
                                Enviar e-mail de boas vindas para Portal do
                                Cliente
                              </Typography>
                              <Tooltip
                                enterTouchDelay={0}
                                arrow
                                placement="right"
                                title="Envie um e-mail explicando o acesso ao Portal de Clientes com instruções e login para seu novo cliente"
                              >
                                <div>
                                  <Icon
                                    icon="info"
                                    sx={{
                                      width: '18px',
                                      height: '18px',
                                      mt: '2px',
                                      color: theme.palette.neutral[700],
                                      cursor: 'pointer'
                                    }}
                                  />
                                </div>
                              </Tooltip>
                            </Stack>
                          </AccordionSummary>
                        </Accordion>
                      )
                    }}
                  />
                  <Controller
                    control={control}
                    name={'consider_active_on_registration'}
                    render={({ field }) => {
                      return (
                        <Accordion
                          expanded={false}
                          onChange={() => {
                            setValue(
                              'consider_active_on_registration',
                              !field.value
                            )
                          }}
                          sx={{
                            border: '1px solid #E8EBE8',
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                            borderRadius: '8px !important',
                            backgroundColor: '#FAFBFA',
                            boxShadow: 'none',
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': {
                              margin: 0,
                              backgroundColor: '#fff'
                            }
                          }}
                        >
                          <AccordionSummary
                            hasSwitch
                            expandIcon={
                              <Switch checked={field.value} {...field} />
                            }
                            sx={{
                              '.MuiAccordionSummary-content': {
                                margin: '20px 0px'
                              }
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <Typography variant="smBold">
                                Tornar ativo somente após o primeiro pagamento
                              </Typography>
                              <Tooltip
                                enterTouchDelay={0}
                                arrow
                                placement="right"
                                title="Quando ativado, o cliente só será considerado ativo após realizar o primeiro pagamento"
                              >
                                <div>
                                  <Icon
                                    icon="info"
                                    sx={{
                                      width: '18px',
                                      height: '18px',
                                      mt: '2px',
                                      color: theme.palette.neutral[700],
                                      cursor: 'pointer'
                                    }}
                                  />
                                </div>
                              </Tooltip>
                            </Stack>
                          </AccordionSummary>
                        </Accordion>
                      )
                    }}
                  />
                  <Controller
                    control={control}
                    name={'cancel_if_first_billing_unpaid'}
                    render={({ field }) => {
                      return (
                        <Accordion
                          expanded={false}
                          onChange={() => {
                            setValue(
                              'cancel_if_first_billing_unpaid',
                              !field.value
                            )
                          }}
                          sx={{
                            border: '1px solid #E8EBE8',
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                            borderRadius: '8px !important',
                            backgroundColor: '#FAFBFA',
                            boxShadow: 'none',
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': {
                              margin: 0,
                              backgroundColor: '#fff'
                            }
                          }}
                        >
                          <AccordionSummary
                            hasSwitch
                            expandIcon={
                              <Switch checked={field.value} {...field} />
                            }
                            sx={{
                              '.MuiAccordionSummary-content': {
                                margin: '20px 0px'
                              }
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <Typography variant="smBold">
                                Cancelar recorrência se a primeira fatura não
                                for paga até a emissão da segunda
                              </Typography>
                              <Tooltip
                                enterTouchDelay={0}
                                arrow
                                placement="right"
                                title="Ao ativar esta opção, a recorrência será automaticamente cancelada caso a primeira fatura não tenha sido paga até a data de emissão da segunda fatura. Isso evita que o cliente continue sendo cobrado sem ter concluído a contratação inicial."
                              >
                                <div>
                                  <Icon
                                    icon="info"
                                    sx={{
                                      width: '18px',
                                      height: '18px',
                                      mt: '2px',
                                      color: theme.palette.neutral[700],
                                      cursor: 'pointer'
                                    }}
                                  />
                                </div>
                              </Tooltip>
                            </Stack>
                          </AccordionSummary>
                        </Accordion>
                      )
                    }}
                  />
                  <Controller
                    control={control}
                    name={'simplifiedCheckout'}
                    render={({ field }) => {
                      return (
                        <Accordion
                          expanded={false}
                          onChange={() => {
                            setValue('simplifiedCheckout', !field.value)
                          }}
                          sx={{
                            border: '1px solid #E8EBE8',
                            borderLeft: `3px solid ${theme.palette.primary.main}`,
                            borderRadius: '8px !important',
                            backgroundColor: '#FAFBFA',
                            boxShadow: 'none',
                            '&:before': { display: 'none' },
                            '&.Mui-expanded': {
                              margin: 0,
                              backgroundColor: '#fff'
                            }
                          }}
                        >
                          <AccordionSummary
                            hasSwitch
                            expandIcon={
                              <Switch checked={field.value} {...field} />
                            }
                            sx={{
                              '.MuiAccordionSummary-content': {
                                margin: '20px 0px'
                              }
                            }}
                          >
                            <Stack
                              direction="row"
                              alignItems="center"
                              gap={0.5}
                            >
                              <Typography variant="smBold">
                                Contratação simplificada (dispensar endereço)
                              </Typography>
                              <Tooltip
                                enterTouchDelay={0}
                                arrow
                                placement="right"
                                title="Remove os campos de endereço do checkout, deixando o formulário mais curto. Válido apenas quando o link aceita Pix e/ou Cartão. O Boleto exige o endereço do pagador, então as duas opções não podem ser usadas juntas."
                              >
                                <div>
                                  <Icon
                                    icon="info"
                                    sx={{
                                      width: '18px',
                                      height: '18px',
                                      mt: '2px',
                                      color: theme.palette.neutral[700],
                                      cursor: 'pointer'
                                    }}
                                  />
                                </div>
                              </Tooltip>
                            </Stack>
                          </AccordionSummary>
                        </Accordion>
                      )
                    }}
                  />
                </>
              )}
            </Stack>
          </Collapse>
        </Box>
      </S.FormSectionWrapper>
      {/* Campos compartilhados usando SharedChargeFields */}
      <SharedChargeFields
        control={control as any}
        watch={watch as any}
        setValue={setValue as any}
        trigger={trigger as any}
        errors={formState.errors as any}
        register={register}
        clearErrors={clearErrors as any}
        isCharge={isCharge}
        setIsCharge={setIsCharge}
        isUnique={isUnique}
        setIsUnique={setIsUnique}
        isEdit={isEdit}
        editType={isUnique ? 'charge' : 'recurrence'}
        isProductOrLink={true}
        hiddenSections={[]}
        externalReplaceDiscounts={externalReplaceDiscounts}
        paymentsTypeRef={paymentsTypeRef}
        contractModels={contractModels}
        contractValidation={contractValidation}
        onContractModelChange={onContractModelChange}
        onAddContractCustomField={onAddContractCustomField}
        onAddAllContractCustomFields={onAddAllContractCustomFields}
      />

      <Dialog
        icon="delete"
        title="Produto removido"
        description="Deseja manter as configurações atuais?"
        isOpenDialog={isOpenDialogDeleteConfig}
        setIsOpenDialog={() => setIsOpenDialogDeleteConfig(false)}
      >
        <Stack direction="row" spacing={2} width="100%">
          <Button
            variantButton="neutral"
            onClick={() => handleDeleteInitialProduct(false)}
            fullWidth
          >
            Remover
          </Button>
          <Button onClick={() => handleDeleteInitialProduct(true)} fullWidth>
            Manter
          </Button>
        </Stack>
      </Dialog>
    </S.FormWrapper>
  )
}

export default CreatePaymentLinkForm
